/**
 * Persist the last app route so we can redirect the user back to where they were
 * (e.g. after Connect Device flow or User Info submit) instead of always sending them to /overview.
 */

const LAST_APP_ROUTE_KEY = "circadia_last_app_route";

/** Paths we consider "main" app routes — we persist these so we can return the user here. */
const MAIN_APP_ROUTES = [
  "/overview",
  "/chat",
  "/care",
  "/reminders",
  // Note: /patient-profile is intentionally omitted — we must not persist it as
  // "last route" or Back from profile would navigate to /patient-profile (no-op).
  "/ai-coach",
  "/settings",
  "/notification-settings",
  "/contact-support",
  "/faq",
  "/feedback",
  "/intended-use",
];

/**
 * Save the current path as the last app route if it's one of the main app routes.
 * Call this when the user navigates (e.g. in Mobile layout on location change).
 */
export function saveLastAppRouteIfApplicable(
  pathname: string,
  search: string = "",
  storage: Storage = typeof window !== "undefined"
    ? localStorage
    : (null as unknown as Storage)
): void {
  if (!storage) return;
  const path = pathname?.split("?")[0] || "";
  if (MAIN_APP_ROUTES.some((r) => path === r || path.startsWith(r + "/"))) {
    const full = search ? `${path}${search}` : path;
    try {
      storage.setItem(LAST_APP_ROUTE_KEY, full);
    } catch {
      // ignore quota or other errors
    }
  }
}

/**
 * Get the last saved app route (path + search), or null if none.
 */
export function getLastAppRoute(
  storage: Storage = typeof window !== "undefined"
    ? localStorage
    : (null as unknown as Storage)
): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(LAST_APP_ROUTE_KEY);
  } catch {
    return null;
  }
}

/**
 * Return the route to navigate to after a "go back to app" action:
 * last route if valid, otherwise defaultPath (e.g. "/overview").
 */
export function getRedirectTarget(defaultPath: string = "/overview"): string {
  const last = getLastAppRoute();
  if (!last || !last.startsWith("/")) return defaultPath;
  // Ignore profile stack — never use as exit target (also fixes legacy bad values in storage)
  if (last === "/patient-profile" || last.startsWith("/patient-profile/")) {
    return defaultPath;
  }
  return last;
}
